'use client';

import type { ProductVariantAvailability } from '@repo/shopify';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

interface LiveAvailabilityState {
	/** `null` while the live pass is still streaming in (or absent a provider). */
	availability: ProductVariantAvailability[] | null;
	/** True only inside a provider — i.e. when a live pass is actually coming. */
	streaming: boolean;
	setAvailability: (availability: ProductVariantAvailability[]) => void;
}

const LiveAvailabilityContext = createContext<LiveAvailabilityState>({
	availability: null,
	streaming: false,
	setAvailability: () => {},
});

/**
 * Holds the live `availableForSale` state for the buy box. The prerendered
 * shell renders from cached variant data; the request-time availability island
 * (see the PDP's `LiveAvailabilityStream`) pushes the live result in through
 * this context as an in-place state update, so the buy box never remounts when
 * live stock state lands.
 */
export function LiveAvailabilityProvider({ children }: { children: ReactNode }) {
	const [availability, setAvailability] = useState<ProductVariantAvailability[] | null>(null);
	const value = useMemo(() => ({ availability, streaming: true, setAvailability }), [availability]);
	return (
		<LiveAvailabilityContext.Provider value={value}>{children}</LiveAvailabilityContext.Provider>
	);
}

/** Rendered by the dynamic island; feeds its payload into the provider. */
export function LiveAvailabilityReceiver({
	availability,
}: {
	availability: ProductVariantAvailability[];
}) {
	const { setAvailability } = useContext(LiveAvailabilityContext);
	useEffect(() => {
		setAvailability(availability);
	}, [availability, setAvailability]);
	return null;
}

export function useLiveAvailability() {
	return useContext(LiveAvailabilityContext);
}
